const api = async (path, options = {}) => {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...options,
    headers: { 'content-type': 'application/json', ...(options.headers || {}) }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `API error ${response.status}`);
  return data;
};

const defaultGame = () => ({
  id: `game_${crypto.randomUUID().replaceAll('-', '').slice(0, 16)}`,
  title: 'Heart150 Demo Quiz',
  description: 'Bộ câu hỏi mẫu để bắt đầu phiên chơi đầu tiên.',
  coverImage: 'assets/game_cover.png',
  timeLimit: 20,
  requirePlayerLogin: false,
  questions: [{
    id: `q_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`,
    type: 'multiple_choice',
    questionText: 'Heart150 giúp biến khán giả thành điều gì?',
    timeLimit: 20,
    options: [
      { id: 'A', text: 'Đám đông phấn khích' },
      { id: 'B', text: 'Người xem thụ động' },
      { id: 'C', text: 'Một bảng tính' },
      { id: 'D', text: 'Một email' }
    ],
    correctOptions: ['A'],
    explanation: 'Heart150 là nền tảng tương tác đám đông trực tiếp.'
  }]
});

import { playerSession } from './playerSession.js';

const playerToken = participantId => {
  const player = playerSession.get();
  return player?.participantId === participantId ? player.playerToken : '';
};

const currentPlayerToken = () => playerSession.get()?.playerToken || '';

export const dbSim = {
  getHostTier() { return localStorage.getItem('h150_host_tier') || 'free'; },
  async setHostTier(tier) { localStorage.setItem('h150_host_tier', tier); },

  async getCurrentUser() {
    try { return (await api('/auth/me')).user; } catch { return null; }
  },

  async getAdminOverview(days = 30) { return api(`/admin/overview?days=${encodeURIComponent(days)}`); },
  async getHostModels() { return api('/admin/host-models?engine=1'); },
  async refreshHostModels() { return api('/admin/host-models/run', { method: 'POST', body: '{}' }); },
  async getAdminQuizzes() { return api('/admin/quizzes'); },
  async getAdminSessions() { return api('/admin/sessions'); },
  async getAdminSystem() { return api('/admin/system'); },

  async getBrand() { return api('/brand'); },
  async saveBrand(brand) { return api('/brand', { method: 'PUT', body: JSON.stringify(brand) }); },

  async getGames() {
    const games = await api('/games');
    if (games.length) return games;
    const seeded = defaultGame();
    await this.saveGame(seeded);
    return [seeded];
  },

  async getGame(id) { return api(`/games/${encodeURIComponent(id)}`).catch(() => null); },
  async getSessionGame(sessionId) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/game`, {
      headers: { 'x-player-token': currentPlayerToken() }
    }).catch(() => null);
  },
  async saveGame(game) { return api('/games', { method: 'PUT', body: JSON.stringify(game) }); },
  async deleteGame(id) { return api(`/games/${encodeURIComponent(id)}`, { method: 'DELETE' }); },
  async setGamePinned(id, pinned) {
    return api(`/games/${encodeURIComponent(id)}/pin`, { method: 'PUT', body: JSON.stringify({ pinned }) });
  },

  async getSessions() { return api('/sessions'); },
  async getSession(id) { return api(`/sessions/${encodeURIComponent(id)}`).catch(() => null); },
  async getSessionByPin(pin) { return api(`/sessions/pin/${encodeURIComponent(pin)}`).catch(() => null); },
  async createSession(gameId, mode = 'live') { return api('/sessions', { method: 'POST', body: JSON.stringify({ gameId, mode }) }); },
  async saveSession(session) {
    return api(`/sessions/${encodeURIComponent(session.id)}`, { method: 'PUT', body: JSON.stringify(session) });
  },

  async getParticipants(sessionId) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/participants`, {
      headers: { 'x-player-token': currentPlayerToken() }
    });
  },
  async addParticipant(sessionId, nickname, email = null) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/participants`, {
      method: 'POST', body: JSON.stringify({ nickname, email })
    });
  },
  async resumePlayer(sessionId) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/player`, {
      headers: { 'x-player-token': currentPlayerToken() }
    });
  },

  async getResponses(sessionId) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/responses`, {
      headers: { 'x-player-token': currentPlayerToken() }
    });
  },
  async submitResponse(participantId, questionId, selectedOption) {
    return api('/responses', {
      method: 'POST',
      headers: { 'x-player-token': playerToken(participantId) },
      body: JSON.stringify({ participantId, questionId, selectedOption })
    });
  },

  async getLeaderboard(sessionId) {
    const parts = await this.getParticipants(sessionId);
    const responses = await this.getResponses(sessionId);
    const correctTime = id => responses.filter(r => r.participantId === id && r.isCorrect)
      .reduce((sum, response) => sum + response.responseTimeMs, 0);
    return parts.sort((a, b) => b.score - a.score || correctTime(a.id) - correctTime(b.id) ||
      new Date(a.joinedAt) - new Date(b.joinedAt));
  },

  async generateQuiz(topic, count = 5, difficulty = 'medium', language = 'vi') {
    return api('/ai/generate-quiz', {
      method: 'POST', body: JSON.stringify({ topic, count, difficulty, language })
    });
  },

  async getSessionReport(sessionId) {
    return api(`/sessions/${encodeURIComponent(sessionId)}/report`).catch(() => null);
  }
};
